<template>
  <div class="members-settings-card h-100" :class="{ 'twitch-theme': isTwitchUser }">
    <div class="members-settings-card-header">
      <h4 class="members-settings-card-title">{{ labels.title }}</h4>
      <p class="members-settings-card-intro">{{ labels.hint }}</p>
    </div>
    <div class="members-settings-card-body">
      <div class="admin-watermark-preview-label">{{ labels.previewLabel }}</div>
      <div class="admin-watermark-preview-stage">
        <img
          v-if="watermarkPreviewUrl"
          :key="watermarkPreviewKey"
          :src="watermarkPreviewUrl"
          :alt="labels.previewAlt"
          class="admin-watermark-preview-image"
          @error="handlePreviewError"
        />
        <div v-else class="admin-watermark-preview-empty">
          {{ labels.emptyState }}
        </div>
      </div>

      <div class="form-group mb-0 mt-3">
        <label for="adminWatermarkFileInput" class="control-label label-uppercase">
          {{ labels.fileLabel }}
        </label>
        <div class="admin-watermark-actions">
          <div class="admin-watermark-file-wrap">
            <input
              id="adminWatermarkFileInput"
              ref="customWatermarkInput"
              type="file"
              class="admin-watermark-file-native"
              accept=".png,image/png"
              tabindex="-1"
              @change="onCustomWatermarkSelected"
            />
            <button
              type="button"
              class="form-control border-input admin-watermark-file-btn text-start"
              @click="onPickClick"
            >
              {{ customWatermarkFileName || labels.browseButton }}
            </button>
          </div>
          <button
            type="button"
            class="btn btn-primary admin-watermark-save-btn"
            :disabled="saveDisabled"
            @click="saveCustomWatermark"
          >
            {{ isSaving ? labels.saving : labels.save }}
          </button>
        </div>
        <small class="form-text text-muted">
          {{ labels.fileHint }}
        </small>
      </div>
    </div>
  </div>
</template>

<script>
import { supabase } from "@/supabase";
import { getEffectiveUserId } from "@/utils/impersonation";
import { useCurrentUserStore } from "@/stores/currentUser";
import { getApiUrl as resolveApiUrl } from "@/api/client";

export default {
  props: {
    isTwitchUser: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      customWatermarkFile: null,
      customWatermarkFileName: "",
      customWatermarkObjectPreviewUrl: null,
      customWatermarkCacheBust: Date.now(),
      customWatermarkPreviewErrored: false,
      customWatermarkLastSavedUrl: "",
      isSaving: false,
    };
  },
  computed: {
    profile() {
      return useCurrentUserStore().profile;
    },
    labels() {
      return {
        title: this.tt("userProfile.adminWatermarkTitle", "Watermark personalizada"),
        hint: this.tt(
          "userProfile.adminWatermarkHint",
          "Esta imagen reemplaza la watermark por defecto de Clipr cuando tu watermark esté activa.",
        ),
        previewLabel: this.tt("userProfile.adminWatermarkPreviewLabel", "Vista previa"),
        previewAlt: this.tt("userProfile.adminWatermarkPreviewAlt", "Vista previa de la watermark"),
        emptyState: this.tt(
          "userProfile.adminWatermarkEmptyState",
          "Sin watermark personalizada. Se usará la watermark por defecto de Clipr.",
        ),
        fileLabel: this.tt("userProfile.adminWatermarkFileLabel", "Archivo PNG"),
        browseButton: this.tt("userProfile.adminWatermarkBrowseButton", "Seleccionar archivo"),
        fileHint: this.tt(
          "userProfile.adminWatermarkFileHint",
          "PNG · máx. 2 MB.",
        ),
        invalidFile: this.tt("userProfile.adminWatermarkInvalidFile", "Solo se permiten archivos PNG (.png)."),
        fileTooLarge: this.tt("userProfile.adminWatermarkFileTooLarge", "El archivo supera el tamaño máximo de 2 MB."),
        saved: this.tt("userProfile.adminWatermarkSaved", "Watermark personalizada actualizada correctamente"),
        saveError: this.tt("userProfile.adminWatermarkSaveError", "Error al guardar la watermark personalizada"),
        sessionError: this.tt(
          "userProfile.adminWatermarkSessionError",
          "Error: sesión no encontrada. Por favor inicia sesión de nuevo.",
        ),
        save: this.tt("userProfile.savePreferences", "Guardar"),
        saving: this.tt("common.saving", "Guardando..."),
      };
    },
    savedWatermarkUrl() {
      const value = String(
        this.customWatermarkLastSavedUrl
        || this.profile?.user_custom_watermark
        || "",
      ).trim();
      return value;
    },
    watermarkPreviewUrl() {
      if (this.customWatermarkObjectPreviewUrl) {
        return this.customWatermarkObjectPreviewUrl;
      }
      if (!this.savedWatermarkUrl || this.customWatermarkPreviewErrored) {
        return "";
      }
      const separator = this.savedWatermarkUrl.includes("?") ? "&" : "?";
      return `${this.savedWatermarkUrl}${separator}v=${this.customWatermarkCacheBust}`;
    },
    watermarkPreviewKey() {
      return this.customWatermarkObjectPreviewUrl || this.watermarkPreviewUrl;
    },
    saveDisabled() {
      return this.isSaving || !this.customWatermarkFile;
    },
  },
  beforeDestroy() {
    this.revokeCustomWatermarkObjectPreview();
  },
  methods: {
    tt(key, fallback) {
      return typeof this.$t === "function" ? this.$t(key) : fallback;
    },
    onPickClick() {
      if (this.isSaving) {
        return;
      }
      const input = this.$refs.customWatermarkInput;
      if (input) {
        input.click();
      }
    },
    revokeCustomWatermarkObjectPreview() {
      if (this.customWatermarkObjectPreviewUrl) {
        URL.revokeObjectURL(this.customWatermarkObjectPreviewUrl);
        this.customWatermarkObjectPreviewUrl = null;
      }
    },
    resetFileInput() {
      const input = this.$refs.customWatermarkInput;
      if (input) {
        input.value = "";
      }
    },
    onCustomWatermarkSelected(event) {
      const input = event?.target;
      const file = input?.files && input.files[0] ? input.files[0] : null;
      if (!file) {
        this.customWatermarkFile = null;
        this.customWatermarkFileName = "";
        this.revokeCustomWatermarkObjectPreview();
        return;
      }

      const name = String(file.name || "").toLowerCase();
      const type = String(file.type || "").toLowerCase();
      const isPng = name.endsWith(".png") && type === "image/png";
      if (!isPng) {
        this.$notify({ message: this.labels.invalidFile, type: "danger" });
        this.customWatermarkFile = null;
        this.customWatermarkFileName = "";
        this.revokeCustomWatermarkObjectPreview();
        this.resetFileInput();
        return;
      }
      const maxBytes = 2 * 1024 * 1024;
      if (Number(file.size || 0) > maxBytes) {
        this.$notify({ message: this.labels.fileTooLarge, type: "danger" });
        this.customWatermarkFile = null;
        this.customWatermarkFileName = "";
        this.revokeCustomWatermarkObjectPreview();
        this.resetFileInput();
        return;
      }

      this.customWatermarkFile = file;
      this.customWatermarkFileName = file.name;
      this.customWatermarkPreviewErrored = false;
      this.revokeCustomWatermarkObjectPreview();
      this.customWatermarkObjectPreviewUrl = URL.createObjectURL(file);
    },
    handlePreviewError() {
      this.customWatermarkPreviewErrored = true;
    },
    async saveCustomWatermark() {
      if (this.saveDisabled) {
        return;
      }
      this.isSaving = true;
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user?.id || !session?.access_token) {
          throw new Error(this.labels.sessionError);
        }
        const targetUserId = getEffectiveUserId(session.user.id);
        if (!targetUserId) {
          throw new Error(this.labels.sessionError);
        }

        const formData = new FormData();
        formData.append("file", this.customWatermarkFile);
        formData.append("user_id", targetUserId);

        const apiUrl = resolveApiUrl();
        const response = await fetch(`${apiUrl}/momo/get-users/admin/custom-watermark`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
          body: formData,
        });
        if (!response.ok) {
          const errorPayload = await response.json().catch(() => null);
          throw new Error(errorPayload?.detail || response.statusText || this.labels.saveError);
        }

        const payload = await response.json().catch(() => null);
        const uploadedUrl = String(
          typeof payload?.user_custom_watermark === "string"
            ? payload.user_custom_watermark
            : typeof payload?.userCustomWatermark === "string"
              ? payload.userCustomWatermark
              : "",
        ).trim();
        if (uploadedUrl) {
          this.customWatermarkLastSavedUrl = uploadedUrl;
        }

        this.customWatermarkCacheBust = Date.now();
        this.customWatermarkPreviewErrored = false;
        this.customWatermarkFile = null;
        this.customWatermarkFileName = "";
        this.resetFileInput();
        this.revokeCustomWatermarkObjectPreview();

        await useCurrentUserStore().refresh(session);
        this.$notify({ message: this.labels.saved, type: "success" });
      } catch (error) {
        this.$notify({
          message: `${this.labels.saveError}: ${error?.message || error}`,
          type: "danger",
        });
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<style scoped>
.members-settings-card {
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  background: #ffffff;
  color: #212529;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.members-settings-card-header {
  padding: 20px 20px 0;
}

.members-settings-card-title {
  margin: 0;
  color: #212529;
  font-size: 1.1rem;
  font-weight: 600;
}

.members-settings-card-intro {
  margin: 0.5rem 0 0;
  color: #6c757d;
  font-size: 0.9rem;
  line-height: 1.45;
}

.members-settings-card-body {
  padding: 15px 20px 20px;
}

.members-settings-card label,
.members-settings-card .form-text,
.members-settings-card .text-muted {
  color: #6c757d;
}

.members-settings-card.twitch-theme {
  border-color: var(--clipr-border-color);
  background: var(--clipr-purple-overlay-medium);
  color: var(--clipr-text-primary);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
}

.members-settings-card.twitch-theme .members-settings-card-title {
  color: var(--clipr-text-primary);
}

.members-settings-card.twitch-theme .members-settings-card-intro,
.members-settings-card.twitch-theme label,
.members-settings-card.twitch-theme .form-text,
.members-settings-card.twitch-theme .text-muted {
  color: var(--clipr-text-secondary);
}

.members-settings-card.twitch-theme .form-control,
.members-settings-card.twitch-theme .border-input {
  border-color: var(--clipr-border-color);
  background: var(--clipr-bg-elevated);
  color: var(--clipr-text-primary);
}

.members-settings-card.twitch-theme .btn-primary {
  border: none;
  border-radius: 8px;
  background: var(--clipr-purple-gradient);
  box-shadow: 0 4px 14px var(--clipr-purple-glow);
}

.admin-watermark-preview-label {
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
}

.admin-watermark-preview-stage {
  min-height: 180px;
  border: 1px dashed rgba(0, 0, 0, 0.2);
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.04);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.twitch-theme .admin-watermark-preview-stage {
  border-color: rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.04);
}

.admin-watermark-preview-image {
  max-width: 100%;
  max-height: 140px;
  object-fit: contain;
}

.admin-watermark-preview-empty {
  font-size: 0.85rem;
  opacity: 0.8;
  text-align: center;
}

.admin-watermark-actions {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}

.admin-watermark-file-wrap {
  flex: 1 1 auto;
  min-width: 0;
}

.admin-watermark-file-native {
  display: none;
}

.admin-watermark-file-btn {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.admin-watermark-save-btn {
  flex: 0 0 auto;
}

@media (max-width: 767.98px) {
  .members-settings-card-header {
    padding: 1rem 1rem 0;
  }

  .members-settings-card-body {
    padding: 0.9rem 1rem 1rem;
  }

  .admin-watermark-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .admin-watermark-file-wrap {
    width: 100%;
  }
}
</style>
